/**
 * Campaign attribution capture.
 *
 * Landing pages already link in with utm_* parameters, and mergeRouteQuery carries
 * those forward through SPA navigation — but only in the URL, which means they are
 * gone the moment the visitor crosses a full page load. Signup, email confirmation
 * and the round trip to Stripe are all full page loads, so today a campaign can be
 * shown to drive traffic and never shown to drive revenue.
 *
 * This keeps them for the length of the decision, so they can be attached to the
 * subscription at checkout and the question "which post produced paying customers"
 * becomes a Stripe query instead of a guess.
 */

const STORAGE_KEY = "rmeAttribution";

// Matches the promo window deliberately: the two are read together and it would be
// confusing for a conversion to carry a discount from one campaign and attribution
// from another.
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

const UTM_KEYS = Object.freeze([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
]);

// Stripe metadata values cap at 500 characters; this is well inside that and long
// enough for any campaign name a human will type.
const MAX_VALUE_LENGTH = 200;

function clean(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, MAX_VALUE_LENGTH);
}

/**
 * Pull the utm_* values out of a route's query.
 *
 * @param {object} query
 * @returns {object} Only the keys actually present.
 */
function utmsFrom(query = {}) {
  const found = {};
  for (const key of UTM_KEYS) {
    const value = clean(query[key]);
    if (value) found[key] = value;
  }
  return found;
}

/**
 * Capture attribution on this navigation, if it carries any.
 *
 * FIRST touch wins: an existing capture is never overwritten while it is still
 * within the window. The question being answered is "what introduced this customer",
 * and last-touch would credit whatever they happened to click on the way back to a
 * decision they had already made.
 *
 * @param {import('vue-router').RouteLocationNormalized} route
 * @returns {object|null} What was stored on this navigation, or null.
 */
export function captureAttributionFromRoute(route) {
  const utms = utmsFrom(route?.query);
  if (Object.keys(utms).length === 0) return null;
  if (getAttribution()) return null;

  const record = {
    ...utms,
    landing_path: clean(route?.path) || "/",
    captured_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...record, capturedAtMs: Date.now() })
    );
  } catch (e) {
    // Storage denied; attribution is best-effort and never blocks a purchase.
  }
  return record;
}

/**
 * @returns {object|null} The stored attribution, or null if absent or expired.
 */
export function getAttribution() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.capturedAtMs || Date.now() - parsed.capturedAtMs > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const { capturedAtMs, ...record } = parsed;
    return Object.keys(record).length ? record : null;
  } catch (e) {
    return null;
  }
}

export function clearAttribution() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {}
}

export { UTM_KEYS, TTL_DAYS as ATTRIBUTION_TTL_DAYS };
